/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationsUpdateSetupTask } from "../funcs/organizationsUpdateSetupTask.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SetupTask } from "../models/components/setuptask.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateSetupTaskRequest,
  UpdateSetupTaskSecurity,
} from "../models/operations/updatesetuptask.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateSetupTaskMutationVariables = {
  request: UpdateSetupTaskRequest;
  security?: UpdateSetupTaskSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateSetupTaskMutationData = SetupTask;

export type UpdateSetupTaskMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateSetupTask organizations
 *
 * @remarks
 * Update one fixed setup task. The request must include at least one effective update: status, assignee, hidden, or clear_assignee=true. Assignee is mutually exclusive with clear_assignee=true.
 */
export function useUpdateSetupTaskMutation(
  options?: MutationHookOptions<
    UpdateSetupTaskMutationData,
    UpdateSetupTaskMutationError,
    UpdateSetupTaskMutationVariables
  >,
): UseMutationResult<
  UpdateSetupTaskMutationData,
  UpdateSetupTaskMutationError,
  UpdateSetupTaskMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateSetupTaskMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateSetupTask(): MutationKey {
  return ["@gram/client", "organizations", "updateSetupTask"];
}

export function buildUpdateSetupTaskMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateSetupTaskMutationVariables,
  ) => Promise<UpdateSetupTaskMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateSetupTask(),
    mutationFn: function updateSetupTaskMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateSetupTaskMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationsUpdateSetupTask(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
